import React, { useReducer, useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthentication } from '@auth/hooks/useAuthentication'
import { IWishlistResort } from '@contexts/WishlistContext'
import { AuthHeaders, useAuthHeaders } from '@hooks/useAuthHeaders'
import MkAccountService from '@account/services/service'
import { rciDepositsReducer } from '@account/utils/reducer'
import {
  Waitlist,
  WaitlistResort,
  WaitlistItemDetails,
  WaitlistItem,
  fetchTrips,
  fetchWaitlistItems,
  fetchWaitlistResorts,
  fetchWaitlistItemDetails as fetchWaitlistItemDetailsApi,
} from '@account/services/vacwaitlist'
import { fetchWishlist, addToWishlist as addToWishlistApi, removeFromWishlist as removeFromWishlistApi } from '@account/services/wishlist'
import { usePointsBenefits } from '@account/hooks/usePointsBenefits'
import { useOwnership } from '@account/hooks/useOwnership'
import { useProfile } from '@account/hooks/useProfile'
import type { IPastTripsYearGroup, IUpcomingTrip, IUpcomingTripsPagination } from '@account/types'
import { createLogger } from '@utils/Logger'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { MkAccountContext, MkAccountContextType, type RefreshTripsOptions } from './_context'
import { useSnackbar } from '@shared/contexts/SnackbarContext'
import { useSiteContext } from '@shared/contexts/MkSiteContext'
import { FAILED_TO_FETCH_TRIPS_EBAC_MESSAGE, FAILED_TO_FETCH_TRIPS_SIR_MESSAGE } from '@shared/constants'

// Global cache keys
const CACHE_KEYS = {
  UPCOMING_VACATIONS: 'mk_account_upcoming_vacations',
  PAST_VACATIONS: 'mk_account_past_vacations',
  WAITLIST: 'mk_account_waitlist',
  WAITLIST_RESORTS: 'mk_account_waitlist_resorts',
  WISHLIST: 'mk_account_wishlist',
  CACHE_TIMESTAMP: 'mk_account_cache_timestamp',
  JWT_TOKEN: 'mk_account_jwt_token',
  CACHED_USER: 'mk_account_cached_user',
}

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000

// Logger instance for AccountProvider
const logger = createLogger('AccountProvider')

// Global cache utilities
const getCacheData = (key: string) => {
  try {
    const data = sessionStorage.getItem(key)
    return data ? JSON.parse(data) : null
  } catch (error) {
    logger.error('Error reading cache:', error)
    return null
  }
}

const setCacheData = (key: string, data: any) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(data))
  } catch (error) {
    logger.error('Error writing cache:', error)
  }
}

const clearCache = () => {
  try {
    Object.values(CACHE_KEYS).forEach((key) => {
      sessionStorage.removeItem(key)
    })
    // Also clear user cache
    sessionStorage.removeItem(CACHE_KEYS.CACHED_USER)
  } catch (error) {
    logger.error('Error clearing cache:', error)
  }
}

const isCacheValid = (jwtToken: string, currentUser?: string) => {
  const cachedToken = getCacheData(CACHE_KEYS.JWT_TOKEN)
  const timestamp = getCacheData(CACHE_KEYS.CACHE_TIMESTAMP)
  const cachedUser = getCacheData(CACHE_KEYS.CACHED_USER)
  const now = Date.now()

  // Check if cache is for a different user
  const isDifferentUser = currentUser && cachedUser && cachedUser !== currentUser

  if (isDifferentUser) {
    logger.info('Cache is for different user, invalidating', {
      cachedUser,
      currentUser,
      timestamp: new Date().toISOString(),
    })
    return false
  }

  return cachedToken === jwtToken && timestamp && now - timestamp < CACHE_DURATION
}

export const MkAccountProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate()
  const { logout, isAuthorized, session } = useAuthentication()
  const [state, dispatch] = useReducer(rciDepositsReducer, MkAccountService.initialState())

  // Points Benefits hook
  const {
    data: pointsBenefitsData,
    loading: pointsBenefitsLoading,
    error: pointsBenefitsError,
    refresh: refreshPointsBenefits,
    reset: resetPointsBenefits,
    getYearSpecificAvailablePoints,
    getCurrentYearAvailablePoints,
  } = usePointsBenefits()

  // Ownership hook
  const { ownershipData, ownershipLoading, ownershipError, refreshOwnership, resetOwnership } = useOwnership()

  // Profile hook
  const { profileData, profileLoading, profileError, refreshProfile, resetProfile } = useProfile()

  const { themeBrand: brand } = useSiteContext()
  const { showSnackbar } = useSnackbar()
  const { chRolloutTripsPagination } = useFlags()

  // Reservation data state
  const [accountWarnings, setAccountWarnings] = useState<string[]>([])

  const [upcomingTrips, setUpcomingTrips] = useState<IUpcomingTrip[]>(() => {
    // In paginated mode the full-list cache would render a stale slice without MkPagination
    // (since `upcomingTripsPagination` is still null). Let the consumer-driven fetch populate it.
    if (chRolloutTripsPagination === true) return []
    const cached = getCacheData(CACHE_KEYS.UPCOMING_VACATIONS)
    return cached || []
  })
  const [upcomingTripsPagination, setUpcomingTripsPagination] = useState<IUpcomingTripsPagination | null>(null)
  const [pastTrips, setPastTrips] = useState<IPastTripsYearGroup[]>(() => getCacheData(CACHE_KEYS.PAST_VACATIONS) || [])
  const [waitlistData, setWaitlistData] = useState<Waitlist>(() => {
    // Initialize from cache if available
    const cached = getCacheData(CACHE_KEYS.WAITLIST)
    return (
      cached || {
        waitlistRequests: { data: [] },
        waitlistEligible: true,
        allowNewRequests: true,
        waitlistCountLimit: 4,
        currentWaitlistCount: 0,
      }
    )
  })
  const [waitlistResorts, setWaitlistResorts] = React.useState<WaitlistResort[]>(() => {
    // Initialize from cache if available
    const cached = getCacheData(CACHE_KEYS.WAITLIST_RESORTS)
    return cached || []
  })
  const [upcomingLoading, setUpcomingLoading] = React.useState(false)
  const [pastLoading, setPastLoading] = React.useState(false)
  const [waitlistLoading, setWaitlistLoading] = React.useState(false)
  const [waitlistResortsLoading, setWaitlistResortsLoading] = React.useState(false)

  // Wishlist state - initialized from session storage cache
  const [wishlistItems, setWishlistItems] = React.useState<IWishlistResort[]>(() => {
    const cached = getCacheData(CACHE_KEYS.WISHLIST)
    return cached || []
  })
  const [wishlistLoading, setWishlistLoading] = React.useState(false)
  const hasFetchedWishlistRef = React.useRef(false)
  const wishlistJwtTokenRef = React.useRef<string | undefined>()
  const wasAuthorizedRef = React.useRef<boolean>(false)

  const authHeaders: AuthHeaders = useAuthHeaders()
  const hasFetchedTripsRef = React.useRef(false)
  const hasFetchedWaitlistRef = React.useRef(false)
  const hasFetchedWaitlistResortsRef = React.useRef(false)
  const jwtTokenRef = React.useRef<string | undefined>()
  /** Monotonic id so only the latest `refreshTrips` call may commit trips/pagination state (avoids page races). */
  const tripsFetchGenerationRef = React.useRef(0)
  /** Tracked separately from JWT to guarantee a reset on membership switch even if backend reissues the same token. */
  const activeMemberNumber = isAuthorized && session?.sessionType === 'authorized' ? (session.authorizedUser?.memberNumber ?? null) : null
  const memberNumberRef = React.useRef<string | null>(activeMemberNumber)

  // Helper to handle 401 errors
  const handleApiError = async (error: any) => {
    const status = typeof error?.status === 'number' ? error.status : undefined
    const messageHas401 = error instanceof Error && error.message && error.message.toString().includes('401')

    if (status === 401 || messageHas401) {
      await logout()
      navigate('/')
      return true
    }
    return false
  }

  const formatTripsErrorMessage = (msg: string) => {
    const [first, ...rest] = msg.split('. ')
    if (!rest.length) return msg
    return (
      <>
        <strong>{first}.</strong> {rest.join('. ')}
      </>
    )
  }

  let snackbarMessage: string | React.ReactNode =
    brand === 'ebac' ? formatTripsErrorMessage(FAILED_TO_FETCH_TRIPS_EBAC_MESSAGE) : formatTripsErrorMessage(FAILED_TO_FETCH_TRIPS_SIR_MESSAGE)

  // Reservation data functions
  const refreshTrips = async (opts: RefreshTripsOptions = {}) => {
    const { forceRefresh = false, pageNum, pageSize } = opts
    const isPaginated = pageNum !== undefined || pageSize !== undefined


    if (!authHeaders['X-JWT-Token'] || !authHeaders['Access-Code']) {
      setUpcomingTrips([])
      setPastTrips([])
      setUpcomingTripsPagination(null)
      hasFetchedTripsRef.current = false
      return
    }
    const currentJwtToken = authHeaders['X-JWT-Token']
    const currentUser = authHeaders['X-User-Name'] || (authHeaders as any)?.userName || 'unknown'
    // Paginated calls always hit the network — each page is a distinct slice.
    if (!isPaginated && !forceRefresh && hasFetchedTripsRef.current && isCacheValid(currentJwtToken, currentUser)) {
      return
    }

    const requestGeneration = ++tripsFetchGenerationRef.current
    const isStale = () => requestGeneration !== tripsFetchGenerationRef.current

    try {
      setUpcomingLoading(true)
      setPastLoading(true)
      const result = await fetchTrips(authHeaders, isPaginated ? { pageNum, pageSize } : undefined)
      if (isStale()) return

      const upcomingList = Array.isArray(result?.upcomingTrips) ? result.upcomingTrips : []
      const pastGroups = Array.isArray(result?.pastTrips) ? result.pastTrips : []
      setUpcomingTrips(upcomingList)
      setPastTrips(pastGroups)
      setUpcomingTripsPagination(result?.upcomingTripsPagination ?? null)
      setAccountWarnings(result?.warnings ?? [])
      if (!isPaginated) {
        // Skip cache writes in paginated mode — per-page caching isn't worth the invalidation cost.
        setCacheData(CACHE_KEYS.UPCOMING_VACATIONS, upcomingList)
        setCacheData(CACHE_KEYS.PAST_VACATIONS, pastGroups)
        setCacheData(CACHE_KEYS.CACHE_TIMESTAMP, Date.now())
        setCacheData(CACHE_KEYS.JWT_TOKEN, currentJwtToken)
        setCacheData(CACHE_KEYS.CACHED_USER, currentUser)
      }
      hasFetchedTripsRef.current = true
    } catch (error) {
      if (isStale()) return
      if (await handleApiError(error)) return
      logger.error('Failed to fetch trips:', error)
      setUpcomingTrips([])
      setPastTrips([])
      setUpcomingTripsPagination(null)
      hasFetchedTripsRef.current = false

      showSnackbar({
        message: snackbarMessage,
        type: 'error',
        duration: 3,
      })
    } finally {
      if (!isStale()) {
        setUpcomingLoading(false)
        setPastLoading(false)
      }
    }
  }

  function setDefaultWaitlistData() {
    setWaitlistData({
      waitlistRequests: { data: [] },
      waitlistEligible: true,
      allowNewRequests: true,
      waitlistCountLimit: 4,
      currentWaitlistCount: 0,
    })
  }

  const refreshWaitlistRequests = async () => {
    if (!authHeaders['X-JWT-Token'] || !authHeaders['Access-Code']) {
      setDefaultWaitlistData()
      hasFetchedWaitlistRef.current = false
      return
    }
    const currentJwtToken = authHeaders['X-JWT-Token']
    try {
      setWaitlistLoading(true)
      const result: Waitlist = await fetchWaitlistItems(authHeaders)
      if (result) {
        setWaitlistData(result)
        setCacheData(CACHE_KEYS.CACHE_TIMESTAMP, Date.now())
        setCacheData(CACHE_KEYS.JWT_TOKEN, currentJwtToken)
        hasFetchedWaitlistRef.current = true
      } else {
        setDefaultWaitlistData()
        hasFetchedWaitlistRef.current = false
      }
    } catch (error) {
      if (await handleApiError(error)) return
      logger.error('Failed to fetch waitlist items:', error)
      setDefaultWaitlistData()
      hasFetchedWaitlistRef.current = false
    } finally {
      setWaitlistLoading(false)
    }
  }

  const refreshWaitlistResorts = useCallback(
    async (forceRefresh = false) => {
      if (!authHeaders['X-JWT-Token'] || !authHeaders['Access-Code']) {
        setWaitlistResorts([])
        hasFetchedWaitlistResortsRef.current = false
        return
      }
      const currentJwtToken = authHeaders['X-JWT-Token']
      if (!forceRefresh && hasFetchedWaitlistResortsRef.current && isCacheValid(currentJwtToken)) {
        return
      }
      try {
        setWaitlistResortsLoading(true)
        const result: WaitlistResort[] = await fetchWaitlistResorts(authHeaders)
        if (result && Array.isArray(result)) {
          setWaitlistResorts(result)
          setCacheData(CACHE_KEYS.WAITLIST_RESORTS, result)
          setCacheData(CACHE_KEYS.CACHE_TIMESTAMP, Date.now())
          setCacheData(CACHE_KEYS.JWT_TOKEN, currentJwtToken)
          hasFetchedWaitlistResortsRef.current = true
        } else {
          setWaitlistResorts([])
          hasFetchedWaitlistResortsRef.current = false
        }
      } catch (error) {
        if (await handleApiError(error)) return
        logger.error('Failed to fetch waitlist resorts:', error)
        setWaitlistResorts([])
        hasFetchedWaitlistResortsRef.current = false
      } finally {
        setWaitlistResortsLoading(false)
      }
    },
    [authHeaders['X-JWT-Token'], authHeaders['Access-Code'], logout, navigate],
  )

  // Optimistic update functions for waitlist operations
  const addWaitlistRequest = useCallback((newRequest: WaitlistItem) => {
    setWaitlistData((prevData) => {
      const updatedData = {
        ...prevData,
        waitlistRequests: {
          ...prevData.waitlistRequests,
          data: [...(prevData.waitlistRequests.data || []), newRequest],
        },
        currentWaitlistCount: prevData.currentWaitlistCount + 1,
      }

      // Update cache with new data
      setCacheData(CACHE_KEYS.WAITLIST, updatedData)
      return updatedData
    })
  }, [])

  const removeWaitlistRequest = useCallback(
    (waitlistId: string) => {
      setWaitlistData((prevData) => {
        const newCount = Math.max(0, prevData.currentWaitlistCount - 1)
        const updatedData = {
          ...prevData,
          waitlistRequests: {
            ...prevData.waitlistRequests,
            data: (prevData.waitlistRequests.data || []).filter((request) => request.waitlistId !== waitlistId),
          },
          currentWaitlistCount: newCount,
        }

        // Update cache with new data
        setCacheData(CACHE_KEYS.WAITLIST, updatedData)
        return updatedData
      })
    },
    [waitlistData.currentWaitlistCount, waitlistData.waitlistCountLimit],
  )

  const updateWaitlistRequest = useCallback((waitlistId: string, updates: Partial<WaitlistItem>) => {
    setWaitlistData((prevData) => {
      const updatedData = {
        ...prevData,
        waitlistRequests: {
          ...prevData.waitlistRequests,
          data: (prevData.waitlistRequests.data || []).map((request) => (request.waitlistId === waitlistId ? { ...request, ...updates } : request)),
        },
      }

      // Update cache with new data
      setCacheData(CACHE_KEYS.WAITLIST, updatedData)
      return updatedData
    })
  }, [])

  // Rollback function to restore previous state if API call fails
  const rollbackWaitlistChanges = useCallback(() => {
    // Force a refresh from the server to restore the correct state
    hasFetchedWaitlistRef.current = false
    refreshWaitlistRequests()
  }, [])

  // Fetch detailed information for a specific waitlist item
  const fetchWaitlistItemDetails = useCallback(
    async (waitlistId: string): Promise<WaitlistItemDetails | null> => {
      if (!authHeaders['X-JWT-Token'] || !authHeaders['Access-Code']) {
        return null
      }
      try {
        const result = await fetchWaitlistItemDetailsApi(waitlistId, authHeaders)
        return result
      } catch (error) {
        if (await handleApiError(error)) return null
        logger.error('Failed to fetch waitlist item details:', error)
        return null
      }
    },
    [authHeaders, logout, navigate],
  )

  useEffect(() => {
    if (state.rciDepositsDraft) {
      MkAccountService.pushRCIDepositsDraft(state.rciDepositsDraft)
    }
  }, [state.rciDepositsDraft])

  // Fetch data when auth headers change
  useEffect(() => {
    const fetchData = async () => {
      const currentJwtToken = authHeaders['X-JWT-Token']
      const currentAccessCode = authHeaders['Access-Code']
      const isPaginationFlagOn = chRolloutTripsPagination === true

      if (currentJwtToken && currentAccessCode) {
        const currentUser = authHeaders['X-User-Name'] || (authHeaders as any)?.userName || 'unknown'

        if (isPaginationFlagOn) {
          // Paginated mode: consumers (MkMyTrips, MkPageHome) own the trips fetch; provider only
          // warms the waitlist/resort caches so unrelated UI stays responsive.
          if (isCacheValid(currentJwtToken, currentUser)) {
            const cachedWaitlist = getCacheData(CACHE_KEYS.WAITLIST)
            const cachedWaitlistResorts = getCacheData(CACHE_KEYS.WAITLIST_RESORTS)
            if (cachedWaitlist) setWaitlistData(cachedWaitlist)
            if (cachedWaitlistResorts) setWaitlistResorts(cachedWaitlistResorts)
            hasFetchedWaitlistRef.current = true
            hasFetchedWaitlistResortsRef.current = true
          }
          jwtTokenRef.current = currentJwtToken
        } else if (isCacheValid(currentJwtToken, currentUser)) {
          try {
            const cachedWaitlist = getCacheData(CACHE_KEYS.WAITLIST)
            const cachedWaitlistResorts = getCacheData(CACHE_KEYS.WAITLIST_RESORTS)

            const result = await fetchTrips(authHeaders)
            const upcomingList = Array.isArray(result?.upcomingTrips) ? result.upcomingTrips : []
            const pastGroups = Array.isArray(result?.pastTrips) ? result.pastTrips : []
            setUpcomingTrips(upcomingList)
            setPastTrips(pastGroups)
            setAccountWarnings(result?.warnings ?? [])
            setCacheData(CACHE_KEYS.UPCOMING_VACATIONS, upcomingList)
            setCacheData(CACHE_KEYS.PAST_VACATIONS, pastGroups)
            if (cachedWaitlist) setWaitlistData(cachedWaitlist)
            if (cachedWaitlistResorts) setWaitlistResorts(cachedWaitlistResorts)

            hasFetchedTripsRef.current = true
            hasFetchedWaitlistRef.current = true
            hasFetchedWaitlistResortsRef.current = true
            jwtTokenRef.current = currentJwtToken
          } catch (error) {
            if (await handleApiError(error)) return
            logger.error('Failed to fetch trips:', error)
            setUpcomingTrips([])
            setPastTrips([])
            hasFetchedTripsRef.current = false

            showSnackbar({
              message: snackbarMessage,
              type: 'error',
              duration: 3,
            })
          } finally {
            setUpcomingLoading(false)
            setPastLoading(false)
          }
        } else if (!hasFetchedTripsRef.current || jwtTokenRef.current !== currentJwtToken) {
          clearCache()
          setAccountWarnings([])
          jwtTokenRef.current = currentJwtToken
          refreshTrips()
        }
      } else {
        clearCache()
        setUpcomingTrips([])
        setPastTrips([])
        setUpcomingTripsPagination(null)
        setWaitlistResorts([])
        setDefaultWaitlistData()
        setAccountWarnings([])
        hasFetchedTripsRef.current = false
        hasFetchedWaitlistRef.current = false
        hasFetchedWaitlistResortsRef.current = false
        jwtTokenRef.current = undefined
      }
    }

    fetchData()
  }, [authHeaders['X-JWT-Token'], authHeaders['Access-Code'], chRolloutTripsPagination])

  // Membership switch: reset cached data and refetch trips/warnings for the new membership.
  // Belt-and-suspenders alongside the JWT-keyed effect above — a switched session always
  // has a different memberNumber, so we always invalidate per-membership UI gating
  // (e.g. delinquent-account banners) without depending on token diffing.
  useEffect(() => {
    if (memberNumberRef.current === activeMemberNumber) return
    const isInitialAssignment = memberNumberRef.current === null
    memberNumberRef.current = activeMemberNumber
    if (isInitialAssignment || !activeMemberNumber) return

    clearCache()
    setAccountWarnings([])
    hasFetchedTripsRef.current = false
    hasFetchedWaitlistRef.current = false
    hasFetchedWaitlistResortsRef.current = false
    // Paginated mode: page consumers own the fetch (URL-driven). They refetch when membership changes.
    if (chRolloutTripsPagination !== true) {
      refreshTrips({ forceRefresh: true })
    }
  }, [activeMemberNumber])

  const accountService = useMemo(() => new MkAccountService(state, dispatch, navigate), [state, dispatch, navigate])

  // Wishlist API handling
  // Only fetches from API on login, otherwise uses session storage cache
  const refreshWishlist = async (forceRefresh = false) => {
    if (!authHeaders['X-JWT-Token'] || !authHeaders['Access-Code']) {
      setWishlistItems([])
      hasFetchedWishlistRef.current = false
      wishlistJwtTokenRef.current = undefined
      return
    }

    const currentJwtToken = authHeaders['X-JWT-Token']

    // Check session storage cache first (unless forcing refresh)
    if (!forceRefresh) {
      const cachedWishlist = getCacheData(CACHE_KEYS.WISHLIST)
      const cachedToken = getCacheData(CACHE_KEYS.JWT_TOKEN)

      // Use cache if it exists and JWT token matches
      if (cachedWishlist && Array.isArray(cachedWishlist) && cachedToken === currentJwtToken) {
        setWishlistItems(cachedWishlist)
        wishlistJwtTokenRef.current = currentJwtToken
        hasFetchedWishlistRef.current = true
        return
      }
    }

    // Only fetch if we haven't fetched yet or JWT token changed
    if (!forceRefresh && hasFetchedWishlistRef.current && wishlistJwtTokenRef.current === currentJwtToken) {
      return
    }

    try {
      setWishlistLoading(true)
      const wishlistData = await fetchWishlist(authHeaders)
      const resorts = wishlistData.resorts || []
      setWishlistItems(resorts)
      setCacheData(CACHE_KEYS.WISHLIST, resorts)
      wishlistJwtTokenRef.current = currentJwtToken
      hasFetchedWishlistRef.current = true
    } catch (error) {
      if (await handleApiError(error)) return
      logger.error('Failed to fetch wishlist:', error)
      setWishlistItems([])
      hasFetchedWishlistRef.current = false
    } finally {
      setWishlistLoading(false)
    }
  }

  const isInWishlist = (productId: string): boolean => wishlistItems.some((resort) => resort.resortId === productId)

  const addToWishlist = async (productId: string): Promise<boolean> => {
    try {
      const success = await addToWishlistApi(productId, authHeaders)
      if (success) {
        // Refresh wishlist to get complete resort data and update cache
        await refreshWishlist(true)
      }
      return success
    } catch (error) {
      if (await handleApiError(error)) return false
      logger.error('Failed to add to wishlist:', error)
      return false
    }
  }

  const removeFromWishlist = async (productId: string): Promise<boolean> => {
    try {
      const success = await removeFromWishlistApi(productId, authHeaders)
      if (success) {
        // Optimistic update: remove from local state and update cache immediately
        setWishlistItems((prev) => {
          const updatedWishlist = prev.filter((resort) => resort.resortId !== productId)
          setCacheData(CACHE_KEYS.WISHLIST, updatedWishlist)
          return updatedWishlist
        })
      }
      return success
    } catch (error) {
      if (await handleApiError(error)) return false
      logger.error('Failed to remove from wishlist:', error)
      return false
    }
  }

  // Only fetch wishlist on login (when user goes from not authorized to authorized)
  // Otherwise, use session storage cache
  useEffect(() => {
    const currentJwtToken = authHeaders['X-JWT-Token']
    const hasAuth = currentJwtToken && authHeaders['Access-Code']

    if (hasAuth) {
      // Check if this is a login (transition from not authorized to authorized)
      const isLogin = !wasAuthorizedRef.current && isAuthorized

      if (isLogin) {
        // User just logged in - fetch wishlist from API
        wasAuthorizedRef.current = true
        wishlistJwtTokenRef.current = currentJwtToken
        refreshWishlist(true)
      } else {
        // User is already logged in - try to load from cache first
        wasAuthorizedRef.current = true
        const cachedWishlist = getCacheData(CACHE_KEYS.WISHLIST)
        const cachedToken = getCacheData(CACHE_KEYS.JWT_TOKEN)

        if (cachedWishlist && Array.isArray(cachedWishlist) && cachedToken === currentJwtToken) {
          // Use cached data
          setWishlistItems(cachedWishlist)
          wishlistJwtTokenRef.current = currentJwtToken
          hasFetchedWishlistRef.current = true
        } else if (!hasFetchedWishlistRef.current || wishlistJwtTokenRef.current !== currentJwtToken) {
          // Cache miss or token changed - fetch from API
          wishlistJwtTokenRef.current = currentJwtToken
          refreshWishlist(true)
        }
      }
    } else {
      // User is not authenticated - clear wishlist
      wasAuthorizedRef.current = false
      setWishlistItems([])
      hasFetchedWishlistRef.current = false
      wishlistJwtTokenRef.current = undefined
    }
  }, [isAuthorized, authHeaders['X-JWT-Token'], authHeaders['Access-Code']])

  const contextValue = useMemo(
    () => ({
      ...accountService.getContextProps<MkAccountContextType>(),
      // Reservation data
      upcomingTrips,
      upcomingTripsPagination,
      accountWarnings,
      pastTrips,
      waitlistData,
      waitlistResorts,
      upcomingLoading,
      pastLoading,
      waitlistLoading,
      waitlistResortsLoading,
      refreshTrips,
      refreshWaitlistRequests,
      refreshWaitlistResorts,
      addWaitlistRequest,
      removeWaitlistRequest,
      updateWaitlistRequest,
      rollbackWaitlistChanges,
      fetchWaitlistItemDetails,
      // Wishlist
      wishlistItems,
      wishlistLoading,
      refreshWishlist,
      addToWishlist,
      removeFromWishlist,
      isInWishlist,
      // Points Benefits
      pointsBenefitsData,
      pointsBenefitsLoading,
      pointsBenefitsError,
      refreshPointsBenefits,
      resetPointsBenefits,
      getYearSpecificAvailablePoints,
      getCurrentYearAvailablePoints,
      // Ownership
      ownershipData,
      ownershipLoading,
      ownershipError,
      refreshOwnership,
      resetOwnership,
      // Profile
      profileData,
      profileLoading,
      profileError,
      refreshProfile,
      resetProfile,
    }),
    [
      accountService,
      upcomingTrips,
      upcomingTripsPagination,
      accountWarnings,
      pastTrips,
      waitlistData,
      waitlistResorts,
      upcomingLoading,
      pastLoading,
      waitlistLoading,
      waitlistResortsLoading,
      addWaitlistRequest,
      removeWaitlistRequest,
      updateWaitlistRequest,
      rollbackWaitlistChanges,
      fetchWaitlistItemDetails,
      // Wishlist
      wishlistItems,
      wishlistLoading,
      refreshWishlist,
      addToWishlist,
      removeFromWishlist,
      isInWishlist,
      // Points Benefits
      pointsBenefitsData,
      pointsBenefitsLoading,
      pointsBenefitsError,
      refreshPointsBenefits,
      resetPointsBenefits,
      getYearSpecificAvailablePoints,
      getCurrentYearAvailablePoints,
      // Ownership
      ownershipData,
      ownershipLoading,
      ownershipError,
      refreshOwnership,
      resetOwnership,
      // Profile
      profileData,
      profileLoading,
      profileError,
      refreshProfile,
      resetProfile,
    ],
  )

  return <MkAccountContext.Provider value={contextValue}>{children}</MkAccountContext.Provider>
}
